/**
 * PublicEventForm — strona dostępna pod /event/:token (BEZ logowania).
 *
 * 5 kroków:
 *   1. Dane wydarzenia + liczba osób
 *   2. Wybór trybu (pakiet / indywidualnie)
 *   3. Wybór dań i ilości
 *   4. Napoje + usługi dodatkowe
 *   5. Alergie + uwagi + podsumowanie → wysłanie
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useParams } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_META = {
  przystawki:     '🥗 Przystawki',
  zupy:           '🍲 Zupy',
  dania_glowne:   '🍖 Dania główne',
  steki:          '🥩 Steki',
  makarony:       '🍝 Makarony',
  burgery:        '🍔 Burgery',
  desery:         '🍰 Desery',
  menu_dzieciece: '🧒 Menu dziecięce',
  napoje:         '🥤 Napoje',
  dodatki:        '➕ Dodatki',
};

const STEPS = [
  { id: 1, label: 'Wydarzenie' },
  { id: 2, label: 'Tryb menu' },
  { id: 3, label: 'Dania' },
  { id: 4, label: 'Napoje & usługi' },
  { id: 5, label: 'Podsumowanie' },
];

const PublicEventForm = () => {
  const { token } = useParams();
  const [bundle, setBundle] = useState(null); // {event, room, menu_items, packages, services}
  const [error, setError] = useState('');
  const [step, setStep] = useState(1);
  const [persons, setPersons] = useState(0);
  const [mode, setMode] = useState('individual'); // package | individual
  const [packageId, setPackageId] = useState('');
  const [itemQty, setItemQty] = useState({}); // {menu_item_id: qty}
  const [serviceQty, setServiceQty] = useState({}); // {service_id: qty}
  const [allergies, setAllergies] = useState('');
  const [clientRemarks, setClientRemarks] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/public-events/${token}`)
      .then((r) => {
        setBundle(r.data);
        setPersons(r.data.event.expected_persons || 0);
        // Set mode from event.allowed_mode
        const allowed = r.data.event.allowed_mode;
        setMode(allowed === 'package' ? 'package' : 'individual');
        // Prefill from existing order
        const existing = r.data.event.client_order;
        if (existing) {
          setPersons(existing.persons || 0);
          setMode(existing.mode || 'individual');
          setPackageId(existing.package_id || '');
          const iq = {}; (existing.items || []).forEach((i) => { iq[i.menu_item_id] = i.qty; });
          setItemQty(iq);
          const sq = {}; (existing.services || []).forEach((s) => { sq[s.service_id] = s.qty; });
          setServiceQty(sq);
          setAllergies(existing.allergies || '');
          setClientRemarks(existing.client_remarks || '');
        }
      })
      .catch((e) => setError(e?.response?.data?.detail || 'Link nieprawidłowy lub wygasł.'));
  }, [token]);

  const submitOrder = async () => {
    if (persons < 1) return toast.error('Wpisz liczbę osób');
    setSubmitting(true);
    try {
      const payload = {
        persons,
        mode,
        package_id: mode === 'package' ? packageId : null,
        items: Object.entries(itemQty).filter(([, q]) => q > 0).map(([menu_item_id, qty]) => ({ menu_item_id, qty: Number(qty) })),
        services: Object.entries(serviceQty).filter(([, q]) => q > 0).map(([service_id, qty]) => ({ service_id, qty: Number(qty) })),
        allergies,
        client_remarks: clientRemarks,
      };
      await axios.post(`${API}/api/public-events/${token}/submit`, payload);
      setSubmitted(true);
      axios.post(`${API}/api/legal/consents`, {
        context: 'form:event-catering',
        documentTypes: ['regulamin', 'privacy'],
      }).catch(() => {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Błąd wysyłki');
    } finally { setSubmitting(false); }
  };

  if (error) return <FullPage><ErrorBox>{error}</ErrorBox></FullPage>;
  if (!bundle) return <FullPage><div style={{ color: '#fff', textAlign: 'center', padding: 40 }}>Ładowanie…</div></FullPage>;
  if (submitted) return <FullPage><SuccessBox event={bundle.event} /></FullPage>;

  const { event, room, menu_items, packages, services } = bundle;
  const activePackages = packages.filter((p) => p.is_active);
  const totalItemsQty = Object.values(itemQty).reduce((s, q) => s + Number(q || 0), 0);

  return (
    <FullPage>
      <div style={card}>
        <Header event={event} />
        <Stepper step={step} />

        {step === 1 && (
          <StepBlock title="📅 Krok 1 — Twoje wydarzenie">
            <InfoGrid>
              <Info label="Data">{event.event_date} {event.event_time}</Info>
              <Info label="Wydarzenie">{event.event_name || '—'}</Info>
              {room && <Info label="Sala">{room.name}</Info>}
              {event.client_notes && <Info label="Uwagi od restauracji" full>{event.client_notes}</Info>}
            </InfoGrid>
            {room?.layout_image && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 700, marginBottom: 6 }}>UKŁAD STOŁÓW</div>
                <img src={room.layout_image} alt="layout" style={{ maxWidth: '100%', borderRadius: 12, border: '1px solid rgba(212,175,55,0.3)' }} />
              </div>
            )}
            <div style={{ marginTop: 16 }}>
              <label style={lbl}>Potwierdź liczbę osób *</label>
              <input
                type="number" min={1} value={persons} onChange={(e) => setPersons(Number(e.target.value))}
                style={{ ...inp, fontSize: 22, fontWeight: 800, textAlign: 'center' }}
                data-testid="public-persons"
              />
            </div>
            <Nav onNext={() => persons >= 1 ? setStep(2) : toast.error('Wpisz liczbę osób')} />
          </StepBlock>
        )}

        {step === 2 && (
          <StepBlock title="🍽️ Krok 2 — Jak chcesz skomponować menu?">
            {event.allowed_mode !== 'individual' && activePackages.length > 0 && (
              <ChoiceBtn
                selected={mode === 'package'}
                onClick={() => setMode('package')}
                title="📦 Wybierz gotowy pakiet"
                desc="Sprawdzony zestaw przygotowany przez restaurację"
              />
            )}
            {event.allowed_mode !== 'package' && (
              <ChoiceBtn
                selected={mode === 'individual'}
                onClick={() => { setMode('individual'); setPackageId(''); }}
                title="✏️ Skomponuj menu sam"
                desc="Wybierz konkretne dania z karty"
              />
            )}

            {mode === 'package' && (
              <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ fontSize: 12, color: '#94a3b8', fontWeight: 700, marginBottom: 4 }}>WYBIERZ PAKIET</div>
                {activePackages.map((p) => (
                  <div key={p.id}
                    onClick={() => setPackageId(p.id)}
                    style={{
                      ...packCard,
                      borderColor: packageId === p.id ? '#d4af37' : 'rgba(212,175,55,0.2)',
                      background: packageId === p.id ? 'rgba(212,175,55,0.15)' : 'rgba(15,23,42,0.6)',
                    }}
                  >
                    <div style={{ fontWeight: 800, fontSize: 15 }}>📦 {p.name}</div>
                    <div style={{ fontSize: 12, color: '#cbd5e1', marginTop: 4 }}>{p.contents_summary || p.description}</div>
                    <div style={{ fontSize: 13, color: '#d4af37', fontWeight: 800, marginTop: 6 }}>
                      {p.custom_price ? 'Cena: do uzgodnienia' : `${p.price_per_person?.toFixed(2)} zł / osoba`}
                      {' · '}min {p.min_persons} os.
                    </div>
                  </div>
                ))}
              </div>
            )}
            <Nav onBack={() => setStep(1)} onNext={() => {
              if (mode === 'package' && !packageId) return toast.error('Wybierz pakiet');
              setStep(3);
            }} />
          </StepBlock>
        )}

        {step === 3 && (
          <StepBlock title="🍴 Krok 3 — Wybierz dania i ilości">
            <MenuPicker
              menuItems={menu_items}
              itemQty={itemQty}
              setItemQty={setItemQty}
              categoriesToShow={['przystawki', 'zupy', 'dania_glowne', 'steki', 'makarony', 'burgery', 'desery', 'menu_dzieciece', 'dodatki']}
            />
            <Nav onBack={() => setStep(2)} onNext={() => setStep(4)} />
          </StepBlock>
        )}

        {step === 4 && (
          <StepBlock title="🥤 Krok 4 — Napoje i usługi dodatkowe">
            <MenuPicker
              menuItems={menu_items}
              itemQty={itemQty}
              setItemQty={setItemQty}
              categoriesToShow={['napoje']}
            />
            {services.length > 0 && (
              <>
                <div style={{ fontSize: 14, fontWeight: 700, marginTop: 14, marginBottom: 8, color: '#d4af37' }}>✨ Usługi dodatkowe</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {services.map((s) => (
                    <ServiceRow key={s.id} service={s} qty={serviceQty[s.id] || 0} setQty={(q) => setServiceQty({ ...serviceQty, [s.id]: q })} />
                  ))}
                </div>
              </>
            )}
            <Nav onBack={() => setStep(3)} onNext={() => setStep(5)} />
          </StepBlock>
        )}

        {step === 5 && (
          <StepBlock title="✅ Krok 5 — Podsumowanie">
            <div style={{ marginBottom: 12 }}>
              <label style={lbl}>⚠️ Alergie pokarmowe (jeśli dotyczą)</label>
              <textarea value={allergies} onChange={(e) => setAllergies(e.target.value)} placeholder="np. orzechy, gluten, laktoza…" style={{ ...inp, minHeight: 60 }} />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={lbl}>💬 Uwagi do zamówienia</label>
              <textarea value={clientRemarks} onChange={(e) => setClientRemarks(e.target.value)} placeholder="Dodatkowe życzenia, sugestie…" style={{ ...inp, minHeight: 60 }} />
            </div>
            <Summary
              persons={persons} mode={mode} packageId={packageId}
              menuItems={menu_items} packages={packages} services={services}
              itemQty={itemQty} serviceQty={serviceQty}
              totalItemsQty={totalItemsQty}
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
              <button onClick={() => setStep(4)} style={btnNav}>← Wróć</button>
              <button onClick={submitOrder} disabled={submitting} style={{ ...btnPrimary, flex: 1 }} data-testid="public-submit">
                {submitting ? 'Wysyłanie…' : '🚀 Wyślij zamówienie'}
              </button>
            </div>
          </StepBlock>
        )}
      </div>
    </FullPage>
  );
};

// ============================================================
//                   SUB-COMPONENTS
// ============================================================
const FullPage = ({ children }) => (
  <div style={{
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 60%, #0f172a 100%)',
    padding: '20px 12px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  }}>
    <div style={{ maxWidth: 720, margin: '0 auto' }}>{children}</div>
  </div>
);
const Header = ({ event }) => (
  <div style={{ textAlign: 'center', marginBottom: 18 }}>
    <div style={{ fontSize: 11, letterSpacing: 4, color: '#d4af37', fontWeight: 800 }}>SYSTEM ZARZĄDZANIA</div>
    <div style={{ fontSize: 28, fontWeight: 900, color: '#fff', letterSpacing: 1, marginTop: 4 }}>MR JERRY</div>
    <div style={{ fontSize: 13, color: '#cbd5e1', marginTop: 6 }}>Konfigurator menu dla: <b style={{ color: '#d4af37' }}>{event.client_name}</b></div>
  </div>
);
const Stepper = ({ step }) => (
  <div style={{ display: 'flex', gap: 6, marginBottom: 14, overflow: 'auto' }}>
    {STEPS.map((s) => (
      <div key={s.id} style={{
        padding: '8px 12px', borderRadius: 999,
        background: step >= s.id ? 'linear-gradient(135deg, #d4af37, #b8860b)' : 'rgba(212,175,55,0.1)',
        color: step >= s.id ? '#fff' : '#94a3b8',
        fontSize: 11, fontWeight: 800, flex: '0 0 auto',
        border: step === s.id ? '2px solid #d4af37' : '1px solid rgba(212,175,55,0.2)',
      }}>
        {s.id}. {s.label}
      </div>
    ))}
  </div>
);
const StepBlock = ({ title, children }) => (
  <div style={{
    background: 'rgba(15,23,42,0.7)',
    border: '1px solid rgba(212,175,55,0.25)',
    borderRadius: 16, padding: 18, color: '#fff',
  }}>
    <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 14, color: '#d4af37' }}>{title}</div>
    {children}
  </div>
);
const Nav = ({ onBack, onNext }) => (
  <div style={{ display: 'flex', gap: 8, marginTop: 18, flexWrap: 'wrap' }}>
    {onBack && <button onClick={onBack} style={btnNav}>← Wróć</button>}
    {onNext && <button onClick={onNext} style={{ ...btnPrimary, flex: 1 }}>Dalej →</button>}
  </div>
);
const InfoGrid = ({ children }) => (
  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>{children}</div>
);
const Info = ({ label, children, full }) => (
  <div style={{ gridColumn: full ? '1 / -1' : 'auto', padding: 10, background: 'rgba(0,0,0,0.3)', borderRadius: 10 }}>
    <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>{label}</div>
    <div style={{ fontSize: 14, color: '#fff', fontWeight: 600, marginTop: 4 }}>{children}</div>
  </div>
);
const ChoiceBtn = ({ selected, onClick, title, desc }) => (
  <button onClick={onClick} style={{
    width: '100%', textAlign: 'left', padding: 14, marginBottom: 8,
    borderRadius: 12, cursor: 'pointer', transition: 'all 0.2s',
    background: selected ? 'rgba(212,175,55,0.2)' : 'rgba(0,0,0,0.3)',
    border: `2px solid ${selected ? '#d4af37' : 'rgba(212,175,55,0.2)'}`,
    color: '#fff',
  }}>
    <div style={{ fontWeight: 800, fontSize: 15 }}>{title}</div>
    <div style={{ fontSize: 12, color: '#cbd5e1', marginTop: 4 }}>{desc}</div>
  </button>
);
const MenuPicker = ({ menuItems, itemQty, setItemQty, categoriesToShow }) => {
  const byCat = useMemo(() => {
    const acc = {};
    menuItems.filter((m) => categoriesToShow.includes(m.category)).forEach((m) => {
      (acc[m.category] = acc[m.category] || []).push(m);
    });
    return acc;
  }, [menuItems, categoriesToShow]);
  if (Object.keys(byCat).length === 0) return <div style={{ color: '#94a3b8', textAlign: 'center', padding: 14 }}>Brak pozycji w menu dla tej kategorii.</div>;
  return (
    <div>
      {categoriesToShow.map((cat) => byCat[cat] && (
        <div key={cat} style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: '#d4af37', marginBottom: 6 }}>{CATEGORY_META[cat] || cat}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {byCat[cat].map((m) => (
              <MenuRow key={m.id} item={m} qty={itemQty[m.id] || 0} setQty={(q) => setItemQty({ ...itemQty, [m.id]: q })} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};
const MenuRow = ({ item, qty, setQty }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px',
    background: qty > 0 ? 'rgba(212,175,55,0.12)' : 'rgba(0,0,0,0.25)',
    border: `1px solid ${qty > 0 ? 'rgba(212,175,55,0.4)' : 'rgba(255,255,255,0.05)'}`,
    borderRadius: 10,
  }}>
    <div style={{ flex: 1 }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>{item.name}</div>
      <div style={{ fontSize: 11, color: '#94a3b8' }}>
        {item.price?.toFixed(2)} zł / {item.unit}
        {item.tags?.length > 0 && <span> · {item.tags.join(', ')}</span>}
        {item.allergens?.length > 0 && <span> · ⚠️ {item.allergens.join(', ')}</span>}
      </div>
    </div>
    <button onClick={() => setQty(Math.max(0, qty - 1))} style={qtyBtn}>−</button>
    <input
      type="number" min={0} value={qty}
      onChange={(e) => setQty(Math.max(0, Number(e.target.value)))}
      style={{ ...inp, width: 50, textAlign: 'center', padding: '6px 4px' }}
      data-testid={`public-menu-qty-${item.id}`}
    />
    <button onClick={() => setQty(qty + 1)} style={qtyBtn}>+</button>
  </div>
);
const ServiceRow = ({ service, qty, setQty }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px',
    background: qty > 0 ? 'rgba(212,175,55,0.12)' : 'rgba(0,0,0,0.25)',
    border: `1px solid ${qty > 0 ? 'rgba(212,175,55,0.4)' : 'rgba(255,255,255,0.05)'}`,
    borderRadius: 10,
  }}>
    <div style={{ flex: 1 }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>✨ {service.name}</div>
      <div style={{ fontSize: 11, color: '#94a3b8' }}>
        {service.price?.toFixed(2)} zł · {service.price_type}
        {service.description && ` · ${service.description}`}
      </div>
    </div>
    {service.allow_quantity ? (
      <>
        <button onClick={() => setQty(Math.max(0, qty - 1))} style={qtyBtn}>−</button>
        <input type="number" min={0} value={qty} onChange={(e) => setQty(Math.max(0, Number(e.target.value)))} style={{ ...inp, width: 50, textAlign: 'center', padding: '6px 4px' }} />
        <button onClick={() => setQty(qty + 1)} style={qtyBtn}>+</button>
      </>
    ) : (
      <input
        type="checkbox"
        checked={qty > 0}
        onChange={(e) => setQty(e.target.checked ? 1 : 0)}
        style={{ width: 22, height: 22, cursor: 'pointer' }}
        data-testid={`public-service-${service.id}`}
      />
    )}
  </div>
);
const Summary = ({ persons, mode, packageId, menuItems, packages, services, itemQty, serviceQty, totalItemsQty }) => {
  const lines = [];
  let total = 0;
  if (mode === 'package' && packageId) {
    const p = packages.find((x) => x.id === packageId);
    if (p) {
      const price = p.custom_price ? 0 : (p.price_per_person || 0);
      lines.push({ label: `📦 Pakiet: ${p.name}`, qty: persons, price, total: price * persons, custom: p.custom_price });
      total += price * persons;
    }
  }
  Object.entries(itemQty).forEach(([id, q]) => {
    if (!q) return;
    const m = menuItems.find((x) => x.id === id);
    if (!m) return;
    const t = (m.price || 0) * q;
    lines.push({ label: `${m.name}`, qty: q, price: m.price || 0, total: t });
    total += t;
  });
  Object.entries(serviceQty).forEach(([id, q]) => {
    if (!q) return;
    const s = services.find((x) => x.id === id);
    if (!s) return;
    let realQty = q;
    if (s.price_type === 'za_osobe') realQty = persons;
    const t = (s.price || 0) * realQty;
    lines.push({ label: `✨ ${s.name}`, qty: realQty, price: s.price || 0, total: t });
    total += t;
  });

  const personsMatch = mode === 'individual' ? totalItemsQty >= persons : true;
  const hasCustom = lines.some((l) => l.custom);

  return (
    <div style={{ background: 'rgba(0,0,0,0.4)', borderRadius: 12, padding: 12, border: '1px solid rgba(212,175,55,0.3)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ fontWeight: 800, color: '#d4af37' }}>Twoje zamówienie:</div>
        <div style={{ fontSize: 12, color: '#94a3b8' }}>👥 {persons} os.</div>
      </div>
      {lines.length === 0 ? (
        <div style={{ color: '#94a3b8', fontSize: 13 }}>Nic jeszcze nie wybrałeś.</div>
      ) : (
        <>
          {lines.map((l, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '4px 0', borderBottom: '1px dashed rgba(255,255,255,0.06)' }}>
              <span style={{ flex: 1 }}>{l.label}</span>
              <span style={{ color: '#94a3b8', minWidth: 40 }}>×{l.qty}</span>
              <span style={{ minWidth: 70, textAlign: 'right', color: '#fff', fontWeight: 700 }}>{l.custom ? '—' : `${l.total.toFixed(2)} zł`}</span>
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10, paddingTop: 8, borderTop: '2px solid #d4af37' }}>
            <span style={{ fontWeight: 800, color: '#d4af37' }}>SUMA:</span>
            <span style={{ fontWeight: 900, color: '#fff', fontSize: 18 }}>{hasCustom ? 'do uzgodnienia' : `${total.toFixed(2)} zł`}</span>
          </div>
        </>
      )}
      {mode === 'individual' && (
        <div style={{ marginTop: 10, padding: 8, borderRadius: 8, background: personsMatch ? 'rgba(34,197,94,0.15)' : 'rgba(245,158,11,0.15)', fontSize: 11, color: personsMatch ? '#22c55e' : '#fbbf24' }}>
          {personsMatch
            ? `✅ Dań w sumie: ${totalItemsQty} (dla ${persons} osób)`
            : `⚠️ Wybranych dań: ${totalItemsQty} — to mniej niż liczba osób (${persons}). Sprawdź czy każdy gość będzie miał danie.`}
        </div>
      )}
    </div>
  );
};
const ErrorBox = ({ children }) => (
  <div style={{ background: 'rgba(239,68,68,0.15)', border: '2px solid rgba(239,68,68,0.4)', borderRadius: 16, padding: 30, textAlign: 'center', color: '#fff' }}>
    <div style={{ fontSize: 40, marginBottom: 10 }}>🚫</div>
    <div style={{ fontSize: 16, fontWeight: 700 }}>{children}</div>
  </div>
);
const SuccessBox = ({ event }) => (
  <div style={{ background: 'rgba(34,197,94,0.15)', border: '2px solid rgba(34,197,94,0.4)', borderRadius: 16, padding: 30, textAlign: 'center', color: '#fff' }}>
    <div style={{ fontSize: 50, marginBottom: 10 }}>🎉</div>
    <div style={{ fontSize: 22, fontWeight: 800, marginBottom: 8 }}>Dziękujemy, {event.client_name}!</div>
    <div style={{ fontSize: 14, color: '#cbd5e1' }}>Twoje zamówienie zostało zapisane. Manager skontaktuje się w razie pytań.</div>
  </div>
);

// ============================================================
//                       STYLES
// ============================================================
const card = { borderRadius: 18 };
const inp = {
  width: '100%', padding: '10px 12px', borderRadius: 10,
  border: '1px solid rgba(212,175,55,0.3)', background: 'rgba(0,0,0,0.4)',
  color: '#fff', fontSize: 14, fontFamily: 'inherit',
};
const lbl = { display: 'block', fontSize: 12, color: '#94a3b8', fontWeight: 700, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 1 };
const btnPrimary = {
  padding: '12px 20px', borderRadius: 12, border: 'none',
  background: 'linear-gradient(135deg, #d4af37, #b8860b)',
  color: '#fff', fontWeight: 800, fontSize: 14, cursor: 'pointer',
};
const btnNav = {
  padding: '12px 18px', borderRadius: 12, border: '1px solid rgba(255,255,255,0.2)',
  background: 'transparent', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer',
};
const qtyBtn = {
  width: 32, height: 32, borderRadius: 8, border: '1px solid rgba(212,175,55,0.4)',
  background: 'rgba(212,175,55,0.15)', color: '#d4af37',
  fontSize: 18, fontWeight: 800, cursor: 'pointer',
};
const packCard = {
  padding: 12, borderRadius: 12, cursor: 'pointer',
  border: '2px solid', transition: 'all 0.2s',
};

export default PublicEventForm;
